/**
 * CR385+CR386: Diagnostics Drawer
 * Shows acquisition engine, URL lineage, Firecrawl block, SCAN link filter diagnostics, and Copy Debug Bundle
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, ExternalLink, ArrowRight, AlertTriangle, Camera, Code2, Filter, Link2, Shield, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  type ParsedResolveNotes,
  getEngineLabel,
  getEngineBadgeVariant,
  buildDebugBundle,
} from "@/lib/read-phase/resolveNotesParser";

interface ReadDiagnosticsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parsed: ParsedResolveNotes | null;
  step: {
    id?: string;
    merchant_id?: string | null;
    status?: string;
    text_char_count?: number | null;
    resolve_extraction_quality_score?: number | null;
    error_message?: string | null;
    text_source_type?: string | null;
  } | null;
}

function UrlDisplay({ label, url }: { label: string; url: string | null }) {
  if (!url) return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-muted-foreground min-w-[80px]">{label}:</span>
      <span className="text-muted-foreground italic">—</span>
    </div>
  );
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-muted-foreground min-w-[80px]">{label}:</span>
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate flex items-center gap-1">
        {url.length > 60 ? url.substring(0, 60) + "…" : url}
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
      </a>
    </div>
  );
}

function LinkSampleList({ links, label }: { links: string[]; label: string }) {
  if (!links || links.length === 0) return null;
  return (
    <div>
      <span className="text-muted-foreground">{label} ({links.length}):</span>
      <div className="mt-1 space-y-0.5">
        {links.slice(0, 5).map((url, i) => (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline truncate block text-[10px]"
          >
            {url.length > 60 ? url.substring(0, 60) + "…" : url}
          </a>
        ))}
      </div>
    </div>
  );
}

export function ReadDiagnosticsDrawer({ open, onOpenChange, parsed, step }: ReadDiagnosticsDrawerProps) {
  if (!parsed || !step) return null;

  const handleCopyDebugBundle = () => {
    const bundle = buildDebugBundle(step, parsed);
    navigator.clipboard.writeText(bundle).then(() => {
      toast.success("Debug bundle copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const hasRedirect = parsed.url_lineage.final_url
    && parsed.url_lineage.requested_url
    && parsed.url_lineage.final_url !== parsed.url_lineage.requested_url;

  const sd = parsed.scan_diag;
  const hasScanDiag = !!sd;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {hasScanDiag ? "SCAN" : "READ"} Diagnostics
                <Badge variant={getEngineBadgeVariant(parsed.acquisition_engine)}>
              {hasScanDiag ? (parsed.engine_reason || "Derived from READ") : getEngineLabel(parsed.acquisition_engine)}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            {step.merchant_id || "Unknown merchant"}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] mt-4">
          <div className="space-y-5">
            {/* Engine section */}
            <div>
              <h4 className="text-sm font-medium mb-2">Acquisition Engine</h4>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Engine:</span>
                  <Badge variant={getEngineBadgeVariant(parsed.acquisition_engine)}>
                    {getEngineLabel(parsed.acquisition_engine)}
                  </Badge>
                </div>
                {parsed.engine_reason && (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
                    {parsed.engine_reason}
                  </div>
                )}
                {!parsed.engine_reason && parsed.acquisition_engine !== "firecrawl_v2" && (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
                    Firecrawl not invoked for this run (engine={parsed.acquisition_engine}).
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* CR388: Blocker Detection */}
            {parsed.blockers_detected && parsed.blockers_detected.length > 0 && (
              <>
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                    <Shield className="h-4 w-4 text-red-500" />
                    Blockers Detected
                  </h4>
                  <div className="space-y-2">
                    {parsed.blockers_detected.map((b, i) => (
                      <div key={i} className="text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded px-2 py-1.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="destructive" className="text-[10px]">{b.type}</Badge>
                          <span className="text-muted-foreground">confidence: {b.confidence.toFixed(2)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {b.evidence.join(", ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* CR388: Variant Runner Results */}
            {parsed.variant_runner && (
              <>
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                    <Zap className="h-4 w-4 text-amber-500" />
                    Variant Runner
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-muted-foreground">Pattern:</span>
                        <Badge variant="outline" className="ml-1 text-[10px]">{parsed.variant_runner.pattern}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Selected:</span>
                        <Badge variant={parsed.variant_runner.selected_score >= 0.4 ? "default" : "destructive"} className="ml-1 text-[10px]">
                          {parsed.variant_runner.selected_variant_id || "none"} ({parsed.variant_runner.selected_score.toFixed(2)})
                        </Badge>
                      </div>
                    </div>
                    {parsed.variant_runner.selected_reason && (
                      <div className="text-muted-foreground">
                        Reason: <span className="font-mono">{parsed.variant_runner.selected_reason}</span>
                      </div>
                    )}
                    <div className="space-y-1.5 mt-2">
                      {parsed.variant_runner.variants_tried.map((v, i) => (
                        <div
                          key={i}
                          className={`rounded px-2 py-1.5 border ${
                            v.variant_id === parsed.variant_runner?.selected_variant_id
                              ? "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/30"
                              : v.failure_reason
                              ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20"
                              : "border-border bg-muted/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-medium">{v.variant_id}</span>
                            <span className="font-mono">{v.score.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                            <span>{v.text_length.toLocaleString()} chars</span>
                            {v.duration_ms != null && <span>{v.duration_ms}ms</span>}
                            {v.failure_reason && (
                              <span className="text-red-600 dark:text-red-400 font-medium">{v.failure_reason}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* CR386: SCAN Link Filter Diagnostics */}
            {hasScanDiag && (
              <>
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                    <Filter className="h-4 w-4" />
                    SCAN Link Filter
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <span className="text-muted-foreground">Links in:</span>
                        <span className="ml-1 font-mono font-medium">{sd.links_in_count}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Kept:</span>
                        <span className="ml-1 font-mono font-medium text-green-600">{sd.links_kept_count ?? "—"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Dropped:</span>
                        <span className="ml-1 font-mono font-medium text-red-600">{sd.links_dropped_count ?? "—"}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-muted-foreground">Candidates:</span>
                        <span className="ml-1 font-mono font-medium">{sd.candidates_emitted_count}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Gate reason:</span>
                <Badge variant={
                          sd.gate_reason === "EMITTED" ? "default" 
                          : sd.gate_reason === "LINKS_SEEDED" ? "default"
                          : sd.gate_reason === "FALLBACK_CANONICAL" || sd.gate_reason === "FALLBACK_CONFIGURED_URL" ? "secondary" 
                          : "destructive"
                        } className="ml-1 text-[10px]">
                          {sd.gate_reason}
                        </Badge>
                      </div>
                    </div>
                    {sd.text_char_count != null && (
                      <div>
                        <span className="text-muted-foreground">Text chars:</span>
                        <span className="ml-1 font-mono">{sd.text_char_count.toLocaleString()}</span>
                      </div>
                    )}

                    {/* Drop reason breakdown */}
                    {sd.drop_reason_counts && Object.values(sd.drop_reason_counts).some(v => v > 0) && (
                      <div className="bg-muted/50 rounded px-2 py-1.5">
                        <span className="text-muted-foreground font-medium">Drop reasons:</span>
                        <div className="grid grid-cols-3 gap-1 mt-1">
                          {Object.entries(sd.drop_reason_counts).map(([reason, count]) => (
                            count > 0 ? (
                              <span key={reason} className="text-[10px]">
                                {reason}: <span className="font-mono">{count}</span>
                              </span>
                            ) : null
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fallback URL */}
                    {sd.fallback_url_used && (
                      <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 rounded px-2 py-1.5">
                        <Link2 className="h-3 w-3" />
                        Fallback URL: {sd.fallback_url_used.length > 50 ? sd.fallback_url_used.substring(0, 50) + "…" : sd.fallback_url_used}
                      </div>
                    )}

                    {/* Kept/Dropped samples */}
                    <LinkSampleList links={sd.kept_samples || []} label="Kept samples" />
                    <LinkSampleList links={sd.dropped_samples || []} label="Dropped samples" />
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* URL Lineage */}
            <div>
              <h4 className="text-sm font-medium mb-2">URL Lineage</h4>
              <div className="space-y-2">
                {parsed.url_lineage.configured_source_id && (
                  <div className="flex items-start gap-2 text-xs">
                    <span className="text-muted-foreground min-w-[80px]">Source ID:</span>
                    <span className="font-mono text-muted-foreground truncate">{parsed.url_lineage.configured_source_id}</span>
                  </div>
                )}
                <UrlDisplay label="Configured" url={parsed.url_lineage.configured_url} />
                <UrlDisplay label="Requested" url={parsed.url_lineage.requested_url} />
                <UrlDisplay label="Final" url={parsed.url_lineage.final_url} />
                {hasRedirect && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1.5">
                    <ArrowRight className="h-3 w-3" />
                    Redirect detected
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Firecrawl block */}
            <div>
              <h4 className="text-sm font-medium mb-2">Firecrawl Diagnostics</h4>
              {parsed.acquisition_engine === "firecrawl_v2" && !parsed.firecrawl && (
                <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1.5 mb-2">
                  <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                  <span>Firecrawl engine selected but diagnostics block missing — check backend resolve_notes writer.</span>
                </div>
              )}
              {parsed.firecrawl ? (
                <div className="space-y-2 text-xs">
                  {parsed.firecrawl.warning?.includes("BUG") && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1.5">
                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                      {parsed.firecrawl.warning}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-muted-foreground">Endpoint:</span>
                      <span className="ml-1 font-mono">{parsed.firecrawl.endpoint}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <span className="ml-1 font-mono">{parsed.firecrawl.status_code ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Links:</span>
                      <span className="ml-1 font-mono">{parsed.firecrawl.links_count ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Links shape:</span>
                      <span className="ml-1 font-mono">{parsed.firecrawl.links_shape ?? "—"}</span>
                    </div>
                  </div>

                  {parsed.firecrawl.warning && !parsed.firecrawl.warning.includes("BUG") && (
                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1.5">
                      <AlertTriangle className="h-3 w-3" />
                      {parsed.firecrawl.warning}
                    </div>
                  )}

                  <LinkSampleList links={parsed.firecrawl.links_sample || []} label="Links sample" />

                  {parsed.firecrawl.javascript_returns && parsed.firecrawl.javascript_returns.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">JS Returns:</span>
                      <pre className="mt-1 bg-muted p-2 rounded overflow-x-auto max-h-24 whitespace-pre-wrap font-mono text-[10px]">
                        {JSON.stringify(parsed.firecrawl.javascript_returns, null, 2)}
                      </pre>
                    </div>
                  )}

                  {parsed.firecrawl.screenshots && parsed.firecrawl.screenshots.length > 0 && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1 mb-1">
                        <Camera className="h-3 w-3" /> Screenshots ({parsed.firecrawl.screenshots.length})
                      </span>
                      <div className="space-y-1">
                        {parsed.firecrawl.screenshots.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline truncate block"
                          >
                            Screenshot {i + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : parsed.acquisition_engine !== "firecrawl_v2" ? (
                <div className="text-xs text-muted-foreground italic">
                  No Firecrawl data (engine was not Firecrawl for this step)
                </div>
              ) : null}
            </div>

            <Separator />

            {/* Step summary */}
            <div>
              <h4 className="text-sm font-medium mb-2">Step Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <span className="ml-1 font-medium">{step.status ?? "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Text source:</span>
                  <span className="ml-1 font-mono">{step.text_source_type ?? "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Chars:</span>
                  <span className="ml-1 font-mono">{step.text_char_count?.toLocaleString() ?? "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Quality:</span>
                  <span className="ml-1 font-mono">{step.resolve_extraction_quality_score?.toFixed(2) ?? "—"}</span>
                </div>
              </div>
              {step.error_message && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1.5">
                  {step.error_message}
                </div>
              )}
            </div>

            {/* Notes */}
            {parsed.notes && (
              <>
                <Separator />
                <div>
                  <h4 className="text-sm font-medium mb-1">Notes</h4>
                  <p className="text-xs text-muted-foreground">{parsed.notes}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Copy Debug Bundle */}
            <Button variant="outline" size="sm" className="w-full" onClick={handleCopyDebugBundle}>
              <Copy className="h-3.5 w-3.5 mr-2" />
              Copy Debug Bundle
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
